import React from 'react'
import { useLocation } from 'react-router'
import { useFeature, useFeatureValue } from '@news-mono/web-common'
import { SeatData, getInterknowledgyData, getSeatsData } from '../data'
import { SeatCard } from '../SeatCard/SeatCard'
import {
    TheSeatEmbedWidgetContainer,
    TheSeatEmbedWidgetTitleContainer,
} from './TheSeatEmbedWidget.styled'
import { FederalElection2025Logo } from '../../../content/FederalElection2025/Logo'
import { ElectionTitleButton } from '../components'
import { WAStateElection2025Logo } from '../../../content/WAStateElection2025/Logo'

export interface TheSeatEmbedWidgetProps {
    mainRef?: React.RefObject<HTMLDivElement>
    fireClickEvent?: () => void
    electorateName?: string | null
    providedData?: SeatData
    isState?: boolean
}

export const TheSeatEmbedWidget = ({
    mainRef,
    electorateName,
    isState,
    providedData,
    fireClickEvent,
}: TheSeatEmbedWidgetProps) => {
    const electionEmbedsEnabled = useFeature('custom-election-embeds')

    const { search } = useLocation()
    const params = new URLSearchParams(search)
    const electionId: number = useFeatureValue('2025-election-id') || 632

    // Check if the current URL contains 'state' or 'federal' to determine logo to use
    if (isState === undefined) {
        isState = search.includes('state')
    }

    // no name or data is provided, so assume it's not an embed, and we want to provide the actual
    // name from the provided url.
    if (!electorateName && !providedData) {
        electorateName = params.get('electorate')
    }

    // get the seat data for the provided electrorate name
    const [seatData, setSeatData] = React.useState<SeatData | undefined>(
        providedData,
    )
    const [isLoading, setIsLoading] = React.useState<boolean>(!providedData)

    // Migrate the loadApiSeatData into a callback to stop it from causing an unmounted component error
    const loadApiSeatData = React.useCallback(async () => {
        let data

        try {
            data = await getInterknowledgyData(electionId)
            const seats = getSeatsData(data)

            // now that we have the seats, let's find one by the name provided

            const seat = seats.find(
                (seat) =>
                    seat.seatName.toLowerCase() ===
                    electorateName?.toLowerCase(),
            )

            setSeatData(seat)
            setIsLoading(false)
        } catch (error) {
            console.error('Error loading interknowlogy data:', error)
        }
    }, [electionId, electorateName])

    // Host the useEffect to call changes whenever one of the dependencies changes
    React.useEffect(() => {
        if (electionEmbedsEnabled && isLoading) {
            loadApiSeatData()
        }
    }, [
        electionId,
        electorateName,
        isLoading,
        loadApiSeatData,
        electionEmbedsEnabled,
    ])

    const ElectionTitleLogo = isState
        ? WAStateElection2025Logo
        : FederalElection2025Logo

    const ViewElectoratesButton = ({
        desktopButton,
    }: {
        desktopButton: boolean
    }) => (
        <ElectionTitleButton
            to="todo"
            includeBorder={true}
            hideOnMobile={desktopButton}
            hideOnDesktop={!desktopButton}
            fireEvent={fireClickEvent}
            text={'View Electorates'}
        />
    )

    return electionEmbedsEnabled ? (
        <TheSeatEmbedWidgetContainer ref={mainRef}>
            <TheSeatEmbedWidgetTitleContainer>
                <ElectionTitleLogo
                    size="small"
                    fireClickEvent={fireClickEvent}
                />
                <ViewElectoratesButton desktopButton={true} />
            </TheSeatEmbedWidgetTitleContainer>
            {seatData ? (
                <SeatCard isEmbed={true} seat={seatData} />
            ) : (
                <SeatCard isLoading isEmbed={true} />
            )}
            <ViewElectoratesButton desktopButton={false} />
        </TheSeatEmbedWidgetContainer>
    ) : null
}
